import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BookCover } from "@/components/BookCover";
import { OrnRule } from "@/components/Ornaments";
import { PageHead } from "@/components/PageHead";
import { Seo } from "@/components/Seo";
import { LIBRARIES, RETAILERS } from "@/content/libraries";
import type { LanguageBinding } from "@/hooks/useLanguage";
import { cx } from "@/lib/classnames";

export const Book: React.FC<{ language: LanguageBinding }> = ({ language }) => {
  const { t, lang, isRTL } = language;
  const base = import.meta.env.BASE_URL;
  // Cover flip state — kept here (not in BookCover) so the button label
  // can be supplied via the page-level translations.
  const [flipped, setFlipped] = useState(false);
  return (
    <>
      <Seo title={t.meta.book.title} description={t.meta.book.description} lang={lang} path="/book" />
      <PageHead
        kicker={t.bookPage.headKicker}
        title={t.bookPage.headTitle}
        tagline={`${t.bookPage.author} · ${t.bookPage.year}`}
        isRTL={isRTL}
      />

      <section className="relative parchment-deckle">
        <div className="grain-soft" />
        <div className="max-w-page mx-auto px-6 md:px-10 py-20 md:py-24 relative">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-5 min-w-0">
              {/* Block-layout wrapper (was a flex container) so the
                  cover's max-width clamp resolves cleanly on mobile.
                  The earlier flex+justify-center caused the BookCover's
                  intrinsic 440px to overflow the viewport because flex
                  items default to min-width:auto. mx-auto centres on
                  mobile, md:mx-0 left-aligns on desktop. */}
              <div className="relative pb-16 mx-auto md:mx-0 w-full max-w-[440px]">
                {/* The drop-shadow lives below the cover; pb-16 above
                    reserves room for the flip button so the shadow and
                    button don't collide on tall layouts. */}
                <div
                  className="absolute bottom-2 inset-x-6 h-16 rounded-full"
                  style={{
                    background: "radial-gradient(ellipse at center, rgba(0,0,0,.35), transparent 70%)",
                    filter: "blur(8px)",
                  }}
                />
                <div className="frame-gold">
                  <BookCover
                    lang="ar"
                    size="lg"
                    flippable
                    flipped={flipped}
                    onFlip={() => setFlipped((f) => !f)}
                    backImageSrc={`${base}covers/ar-back-cover.jpg`}
                    flipLabel={flipped ? t.bookPage.flipToFront : t.bookPage.flipToBack}
                    backAlt={isRTL
                      ? "الغلاف الخلفي — نبذة عن المؤلف"
                      : "Back cover — about the author"}
                  />
                </div>
              </div>
            </div>
            <div className="md:col-span-7 min-w-0">
              <h2
                className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif")}
                style={{
                  fontSize: "clamp(1.6rem, 2.5vw, 2.2rem)",
                  fontWeight: 600,
                  color: "var(--ink)",
                }}
              >
                {t.bookPage.overviewTitle}
              </h2>
              <div
                className={cx(isRTL ? "ff-ar-sans" : "ff-en-body", "mt-6 text-lg leading-relaxed space-y-5")}
                style={{ color: "var(--ink-2)" }}
              >
                {t.bookPage.overview.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>

              {/* Academic-holdings line — compact text-only chips that
                  sit immediately under the book description so the
                  proof of academic provenance reads as part of "what
                  this book is", not as a separate retail-feeling
                  section. Libraries hold the book; they don't sell it,
                  so this row stays visually distinct from the order
                  CTA and the retailer chips below. */}
              <div className="mt-8">
                <div
                  className={cx(
                    isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                    "text-[10px] mb-2",
                  )}
                  style={{ color: "var(--muted-light)" }}
                >
                  {t.bookPage.librariesKicker}
                </div>
                <ul className="flex flex-wrap gap-2">
                  {LIBRARIES.map((lib) => (
                    <li key={lib.id}>
                      <a
                        href={lib.url}
                        target="_blank"
                        rel="noreferrer"
                        className={cx(
                          isRTL ? "ff-ar-sans" : "ff-en-body",
                          "inline-block max-w-full break-words text-xs px-3 py-1 rounded-full transition-opacity hover:opacity-80",
                        )}
                        style={{
                          color: "var(--ink-2)",
                          border: "1px solid var(--hairline-light)",
                          background: "var(--card-surface-warm)",
                        }}
                      >
                        {lib.name[lang]}
                        {lib.note && (
                          <span
                            className="ms-2 opacity-70"
                            style={{ color: "var(--vermillion)" }}
                          >
                            · {lib.note[lang]}
                          </span>
                        )}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10">
                <Link
                  to="/contact"
                  className={cx(
                    isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                    "text-[13px] md:text-[14px] px-6 py-3.5 rounded-full",
                  )}
                  style={{ background: "var(--vermillion)", color: "#fff3df" }}
                >
                  {t.bookPage.orderCta}
                </Link>
              </div>

              {/* Retailer chips — directly under the order CTA so the
                  user sees them precisely when they're deciding "OK,
                  how do I get this?". Hidden entirely when the
                  RETAILERS array is empty (currently the case — every
                  online listing was posting stale 2015 pricing). When
                  reinstated this section reappears with no UI change. */}
              {RETAILERS.length > 0 && (
              <div className="mt-6">
                <div
                  className={cx(
                    isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                    "text-[10px] mb-2",
                  )}
                  style={{ color: "var(--muted-light)" }}
                >
                  {t.bookPage.retailersTitle}
                </div>
                <ul className="flex flex-wrap gap-2">
                  {RETAILERS.map((r) => (
                    <li key={r.id}>
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noreferrer"
                        className={cx(
                          isRTL ? "ff-ar-sans" : "ff-en-body",
                          "inline-block max-w-full break-words text-xs px-3 py-1 rounded-full transition-opacity hover:opacity-80",
                        )}
                        style={{
                          color: "var(--ink)",
                          border: "1px solid var(--vermillion)",
                          background: "transparent",
                        }}
                      >
                        {r.name[lang]} ↗
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              )}

              <div className="mt-16">
                <h3
                  className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif")}
                  style={{ fontSize: "1.8rem", fontWeight: 600, color: "var(--ink)" }}
                >
                  {t.bookPage.whyTitle}
                </h3>
                <div className="mt-6 grid sm:grid-cols-2 gap-x-10 gap-y-8">
                  {t.bookPage.why.map((w, i) => (
                    <div key={i}>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="cat-num text-[13px]" style={{ color: "var(--vermillion)" }}>
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span
                          className="flex-1 h-px"
                          style={{ background: "var(--hairline-light)" }}
                        />
                      </div>
                      <div
                        className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif", "text-xl mb-1")}
                        style={{ color: "var(--ink)", fontWeight: 600 }}
                      >
                        {w.t}
                      </div>
                      <div
                        className={cx(isRTL ? "ff-ar-sans" : "ff-en-body")}
                        style={{ color: "var(--muted-light)" }}
                      >
                        {w.b}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative" style={{ background: "var(--ink)" }}>
        <div className="grain" />
        <div
          className="max-w-[1000px] mx-auto px-6 md:px-10 py-20 md:py-24 text-center relative"
          style={{ color: "var(--parchment)" }}
        >
          <div
            className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-widest", "text-[11px] mb-6")}
            style={{ color: "var(--vermillion-2)" }}
          >
            {t.bookPage.sampleTitle}
          </div>
          <blockquote
            className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif italic", "leading-[1.35]")}
            style={{ fontSize: "clamp(1.4rem, 2.6vw, 2.2rem)", color: "var(--parchment)" }}
          >
            {t.bookPage.sample}
          </blockquote>
          <div className="mt-8 flex justify-center" style={{ color: "var(--gold)" }}>
            <OrnRule />
          </div>
          <div
            className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide", "text-[11px] mt-4")}
            style={{ color: "var(--muted-dark)" }}
          >
            {t.bookPage.sampleSig}
          </div>
        </div>
      </section>

      <section className="relative parchment-deckle">
        <div className="grain-soft" />
        <div className="max-w-page mx-auto px-6 md:px-10 py-20 md:py-24 relative">
          <h3
            className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif", "mb-10")}
            style={{
              fontSize: "clamp(1.8rem, 3vw, 2.4rem)",
              fontWeight: 600,
              color: "var(--ink)",
            }}
          >
            {t.bookPage.orderTitle}
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {t.bookPage.order.map((o, i) => (
              <div
                key={i}
                className="p-8"
                style={{
                  border: "1px solid var(--hairline-light)",
                  background: "var(--card-surface-warm)",
                }}
              >
                <div className="cat-num mb-4 text-lg" style={{ color: "var(--vermillion)" }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div
                  className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif", "text-xl mb-2")}
                  style={{ color: "var(--ink)", fontWeight: 600 }}
                >
                  {o.t}
                </div>
                <div
                  className={cx(isRTL ? "ff-ar-sans" : "ff-en-body")}
                  style={{ color: "var(--muted-light)" }}
                >
                  {o.b}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Endorsements / Testimonials section removed — was placeholder
          copy with no real reader endorsements collected yet, and
          felt off-tone next to the rest of the Book page. The
          translation keys (bookPage.endorseTitle / endorsePlaceholder)
          are kept in src/content/translations.ts so a future iteration
          can reinstate the section trivially when real testimonials
          arrive. */}
    </>
  );
};
